import { useRef, useState } from 'react';
import { useFormacao } from '@/context/FormacaoContext';

interface Props {
  participanteId: string;
  formacaoId: string;
  certificadoUrl: string | null;
  certificadoEmitido: boolean;
  /** Modo inline: sem borda exterior, para usar dentro de modais */
  compact?: boolean;
}

const TIPOS_ACEITES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_MB = 10;

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileName(path: string) {
  return path.split('/').pop() ?? path;
}

export default function CertificadoUploadSection({
  participanteId,
  formacaoId,
  certificadoUrl,
  certificadoEmitido,
  compact = false,
}: Props) {
  const { uploadCertificadoParticipante, removerCertificadoParticipante, getDocumentoUrl, saving } = useFormacao();
  const inputRef = useRef<HTMLInputElement>(null);

  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmRemover, setConfirmRemover] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  function validateFile(file: File): string | null {
    if (!TIPOS_ACEITES.includes(file.type)) {
      return 'Formato não suportado. Use PDF, JPG ou PNG.';
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      return `Ficheiro demasiado grande. Máximo ${MAX_MB} MB.`;
    }
    return null;
  }

  async function handleFile(file: File) {
    setFileError(null);
    setUploadSuccess(false);
    const err = validateFile(file);
    if (err) { setFileError(err); return; }
    setUploading(true);
    const result = await uploadCertificadoParticipante(participanteId, formacaoId, file);
    setUploading(false);
    if (result) {
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    } else {
      setFileError('Erro ao fazer upload. Tente novamente.');
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  async function handleRemover() {
    if (!certificadoUrl) return;
    await removerCertificadoParticipante(participanteId, certificadoUrl);
    setConfirmRemover(false);
  }

  const publicUrl = certificadoUrl ? getDocumentoUrl(certificadoUrl) : null;
  const isPdf = certificadoUrl?.toLowerCase().endsWith('.pdf');

  // ── Tem certificado ──────────────────────────────────────────────────────
  if (certificadoUrl) {
    return (
      <div className={`${compact ? '' : 'border border-zinc-100 rounded-xl p-4'}`}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 flex-shrink-0">
              <i className="ri-medal-line text-sm text-emerald-600" />
            </div>
            <span className="text-[12px] font-semibold text-zinc-700">Certificado</span>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Download */}
            <a
              href={publicUrl ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${isPdf ? 'ri-file-pdf-line' : 'ri-image-line'} text-sm`} />
              </div>
              Ver / Download
            </a>
            {/* Substituir */}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading || saving}
              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer disabled:opacity-40"
              title="Substituir certificado"
            >
              <i className="ri-upload-2-line text-sm" />
            </button>
            {/* Remover */}
            {confirmRemover ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleRemover}
                  disabled={saving}
                  className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 hover:bg-rose-600 px-2 py-1 rounded-lg cursor-pointer disabled:opacity-50"
                >
                  Remover
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRemover(false)}
                  className="whitespace-nowrap text-[11px] text-zinc-500 bg-zinc-100 px-2 py-1 rounded-lg cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmRemover(true)}
                className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                title="Remover certificado"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            )}
          </div>
        </div>

        {/* Preview nome do ficheiro */}
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={`${isPdf ? 'ri-file-pdf-line text-rose-500' : 'ri-image-line text-zinc-500'} text-sm`} />
          </div>
          <span className="text-[12px] text-emerald-700 font-medium truncate flex-1">
            {getFileName(certificadoUrl)}
          </span>
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-sm text-emerald-500" />
          </div>
        </div>

        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleInputChange} />
        {fileError && <p className="text-[11px] text-rose-500 mt-1.5">{fileError}</p>}
      </div>
    );
  }

  // ── Sem certificado ──────────────────────────────────────────────────────
  return (
    <div className={`${compact ? '' : 'border border-zinc-100 rounded-xl p-4'}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
          <i className="ri-medal-line text-sm text-zinc-400" />
        </div>
        <span className="text-[12px] font-semibold text-zinc-700">
          Certificado
          {!certificadoEmitido && (
            <span className="text-[10px] font-normal text-zinc-400 ml-1">opcional</span>
          )}
        </span>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-5 px-4 transition-all cursor-pointer ${
          dragOver
            ? 'border-zinc-400 bg-zinc-50'
            : uploadSuccess
            ? 'border-emerald-300 bg-emerald-50'
            : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50/60'
        } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
      >
        {uploading ? (
          <>
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-loader-4-line text-2xl animate-spin text-zinc-400" />
            </div>
            <p className="text-[12px] text-zinc-500 font-medium">A fazer upload...</p>
          </>
        ) : uploadSuccess ? (
          <>
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100">
              <i className="ri-check-line text-lg text-emerald-600" />
            </div>
            <p className="text-[12px] text-emerald-600 font-semibold">Upload concluído!</p>
          </>
        ) : (
          <>
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-zinc-100">
              <i className="ri-upload-cloud-2-line text-xl text-zinc-400" />
            </div>
            <div className="text-center">
              <p className="text-[13px] font-semibold text-zinc-600">
                {dragOver ? 'Soltar aqui' : 'Clique ou arraste o certificado'}
              </p>
              <p className="text-[11px] text-zinc-400 mt-0.5">PDF, JPG ou PNG · máx. {MAX_MB} MB</p>
            </div>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={handleInputChange}
      />
      {fileError && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-sm text-rose-500" />
          </div>
          <p className="text-[11px] text-rose-500">{fileError}</p>
        </div>
      )}
    </div>
  );
}
